import { useCallback, useEffect, useRef, useState } from "react";

import { askAssistant } from "api/services/chat/assistant";

import { AssistantAnswer, IUseAssistantConversationReturn } from "../types";

export function useAssistantConversation(
  page: string | undefined,
  identity: string
): IUseAssistantConversationReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [submittedQuestion, setSubmittedQuestion] = useState("");
  const [answer, setAnswer] = useState<AssistantAnswer>();
  const [isPending, setIsPending] = useState(false);
  const generation = useRef(0);
  const isRequestInFlight = useRef(false);

  useEffect(() => {
    generation.current += 1;
    isRequestInFlight.current = false;
    setQuestion("");
    setSubmittedQuestion("");
    setAnswer(undefined);
    setIsPending(false);
    setIsOpen(false);
    return () => {
      generation.current += 1;
    };
  }, [page, identity]);

  const handleAsk = useCallback(
    async (prompt?: string) => {
      const requestedQuestion = (prompt ?? question).trim();
      if (!requestedQuestion || isPending || isRequestInFlight.current) return;
      const current = generation.current;
      isRequestInFlight.current = true;
      if (prompt !== undefined) setQuestion(prompt);
      setSubmittedQuestion(requestedQuestion);
      setIsPending(true);
      setAnswer(undefined);
      try {
        const result = await askAssistant({
          question: requestedQuestion,
        });
        if (current === generation.current) {
          setAnswer(result);
          setQuestion("");
        }
      } catch {
        // The shared Apollo request layer owns error feedback.
      } finally {
        if (current === generation.current) {
          isRequestInFlight.current = false;
          setIsPending(false);
        }
      }
    },
    [question, isPending]
  );

  const handleOpen = useCallback(() => setIsOpen(true), []);

  const handleClose = useCallback(() => {
    generation.current += 1;
    isRequestInFlight.current = false;
    setQuestion("");
    setSubmittedQuestion("");
    setAnswer(undefined);
    setIsPending(false);
    setIsOpen(false);
  }, []);

  const handleQuestionChange = useCallback(
    (value: string) => setQuestion(value),
    []
  );

  return {
    isOpen,
    handleOpen,
    handleClose,
    question,
    handleQuestionChange,
    answer,
    submittedQuestion,
    isPending,
    handleAsk,
  };
}
